import React, { useEffect, useMemo, useState } from "react";
import { progressEvent, laneFromLabel, LANE_NAME, BOOK_LABEL, BOOK_FEE_LINE } from "../lib/progress";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import FloatingBookButton from "../components/FloatingBookButton";
import { appUserAuth, api } from "../lib/api";
import { useVisitorProfile } from "../components/VisitorBanner";
import { ArrowRight, ArrowLeft, Sparkle, CheckCircle, Lock, ArrowClockwise } from "@phosphor-icons/react";

const DRAFT_KEY = "exodus_quiz_draft_v1";

// ── Hook pool — Variant A (intro) + Variant B (mid-funnel) + segmented overrides ──
const HOOKS = {
  intro: [
    {
      id: "A",
      eyebrow: "Customer Intake — 12 Questions",
      headline: <>Tell me where you actually want to be. <span className="gold-text italic">I'll show you the way there.</span></>,
      sub: "Twelve questions. About five minutes. No fluff, no salesy tricks. Some are direct. A few will ask you to dream. At the end you'll get a clear pointer to which strategy fits — Annuity, IUL, or Whole Life.",
    },
    {
      id: "B",
      eyebrow: "4-Minute Strategic Fit",
      headline: <>Most prospects are <span className="gold-text italic">one product away</span> from a tax-free retirement.</>,
      sub: "Find your fit in 5 minutes. Twelve questions. Real math, no marketing. The answer at the end is yours to keep — book a call only if it helps.",
    },
  ],
  // Mid-funnel reinforcer that runs at step 5 — segmented by what we already know
  midByGoal: {
    income_for_life: {
      eyebrow: "Halfway there",
      headline: <>A guaranteed paycheck. <span className="gold-text italic">For life.</span></>,
      sub: "Three more questions. The annuity world has 30 flavors — your answers narrow it to one.",
    },
    tax_free_wealth: {
      eyebrow: "Halfway there",
      headline: <>The IRS doesn't tax the rich. <span className="gold-text italic">It taxes the unprepared.</span></>,
      sub: "You're three questions away from seeing how a high-cap IUL becomes your tax-free bucket.",
    },
    family_protection: {
      eyebrow: "Halfway there",
      headline: <>Bulletproof your family. <span className="gold-text italic">While you're alive AND after.</span></>,
      sub: "Three questions left. Permanent life isn't one product — it's a designed structure.",
    },
    generational_legacy: {
      eyebrow: "Halfway there",
      headline: <>Wealth they inherit. <span className="gold-text italic">Not a tax bill.</span></>,
      sub: "Three questions left. The 40% estate hit is avoidable — by design, not by accident.",
    },
    wealth_while_alive: {
      eyebrow: "Halfway there",
      headline: <>Cash you can actually USE. <span className="gold-text italic">While you're alive.</span></>,
      sub: "Three questions left. The infinite-banking concept lives or dies on the right policy structure.",
    },
  },
  midByAge: {
    "25_34": {
      eyebrow: "Halfway there",
      headline: <>You have <span className="gold-text italic">the most valuable asset in finance:</span> time.</>,
      sub: "Compounding from 28 to 65 is mathematically dominant. Three questions left — they shape the design.",
    },
    "55_64": {
      eyebrow: "Halfway there",
      headline: <>Crystallization phase. <span className="gold-text italic">Income certainty matters more than upside now.</span></>,
      sub: "Three questions left. Sequence-of-returns risk is the silent retirement killer — we'll solve for it.",
    },
    "65_plus": {
      eyebrow: "Halfway there",
      headline: <>Distribution chapter. <span className="gold-text italic">You've earned certainty.</span></>,
      sub: "Three questions left. The right structure converts what you have into a paycheck for life.",
    },
  },
};

function pickIntroHook() {
  // Stable per-visitor variant via localStorage (deterministic A/B split)
  let v = localStorage.getItem("exodus_hook_variant");
  if (!v) {
    v = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem("exodus_hook_variant", v);
  }
  return HOOKS.intro.find((h) => h.id === v) || HOOKS.intro[0];
}

export default function Quiz() {
  const visitor = useVisitorProfile();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [identity, setIdentity] = useState({ name: appUserAuth.fullName() || "", email: appUserAuth.email() || "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [resumeOffered, setResumeOffered] = useState(false);

  const introHook = useMemo(pickIntroHook, []);

  useEffect(() => {
    api.get("/quiz/questions").then((r) => setQuestions(r.data.questions || [])).catch(() => {});
    // Pre-fill identity if visitor profile exists
    const p = visitor.get();
    if (p) setIdentity((s) => ({ ...s, name: p.name || "", email: p.email || "" }));

    // Offer resume if a draft exists
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (draft && (Object.keys(draft.answers || {}).length > 0 || draft.identity?.email)) {
        setResumeOffered(true);
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft as user answers
  useEffect(() => {
    if (step === 0) return;
    const draft = { answers, identity, step };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [answers, identity, step]);

  const totalSteps = questions.length;
  const inQuestion = step >= 1 && step <= totalSteps;
  const isIdentity = step === totalSteps + 1;
  const isResult = step === totalSteps + 2;
  const q = inQuestion ? questions[step - 1] : null;

  const progressPct = useMemo(() => {
    if (step === 0) return 0;
    if (isResult) return 100;
    const n = isIdentity ? totalSteps + 1 : step;
    return Math.round((n / (totalSteps + 1)) * 100);
  }, [step, totalSteps, isIdentity, isResult]);

  // Mid-funnel hook — appears at step 5 as a brief interstitial
  const midHook = useMemo(() => {
    const g = answers.q2_primary_goal;
    const goal = Array.isArray(g) ? g[0] : g;
    const age = answers.q1_age_bracket;
    if (goal && HOOKS.midByGoal[goal]) return HOOKS.midByGoal[goal];
    if (age && HOOKS.midByAge[age]) return HOOKS.midByAge[age];
    return null;
  }, [answers]);

  const showMidHookHere = inQuestion && step === 5 && midHook && !answers.__mid_seen;

  const setAnswer = (qid, value, qType, maxSelect) => {
    setAnswers((prev) => {
      if (qType === "ranked") {
        const cur = Array.isArray(prev[qid]) ? prev[qid] : [];
        if (cur.includes(value)) {
          // Toggle off — remove
          return { ...prev, [qid]: cur.filter((v) => v !== value) };
        }
        // Add — respect maxSelect (default 3)
        const cap = maxSelect || 3;
        if (cur.length >= cap) {
          toast.error(`You can only rank ${cap}. Tap one to remove it.`);
          return prev;
        }
        return { ...prev, [qid]: [...cur, value] };
      }
      if (qType === "multi") {
        const cur = Array.isArray(prev[qid]) ? prev[qid] : [];
        const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
        return { ...prev, [qid]: next };
      }
      return { ...prev, [qid]: value };
    });
  };

  const canAdvance = () => {
    if (!q) return true;
    if (!q.required) return true;
    const val = answers[q.id];
    if (q.type === "multi" || q.type === "ranked") return Array.isArray(val) && val.length > 0;
    return !!val;
  };

  const next = () => {
    if (inQuestion && !canAdvance()) {
      toast.error("Please pick an answer to continue.");
      return;
    }
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const resumeDraft = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (draft) {
        setAnswers(draft.answers || {});
        setIdentity(draft.identity || identity);
        setStep(draft.step || 1);
      }
      setResumeOffered(false);
    } catch {
      setResumeOffered(false);
    }
  };

  const dismissResume = () => {
    localStorage.removeItem(DRAFT_KEY);
    setResumeOffered(false);
  };

  const submit = async () => {
    if (!identity.name || !identity.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/quiz/submit", {
        name: identity.name,
        email: identity.email,
        phone: identity.phone,
        answers,
      });
      const rec = r.data.recommendation;
      // Attach the brief PDF URL so the result view can offer it as a download
      rec.brief_pdf_url = r.data.brief_pdf_url;
      // Persist for personalized Learn / Calculator / Reactivation deep-links
      try {
        localStorage.setItem("ex_user_recommendation", JSON.stringify(rec));
      } catch {}
      setResult(rec);
      // Persist visitor profile for retention banner
      visitor.set({
        name: identity.name,
        email: identity.email,
        primary_label: rec.primary_label,
        next_step: rec.next_step,
        completed_at: new Date().toISOString(),
      });
      localStorage.removeItem(DRAFT_KEY);
      progressEvent("fit_complete", { lane: rec.primary || laneFromLabel(rec.primary_label), label: rec.primary_label });
      setStep(totalSteps + 2);
    } catch {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quiz-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <div className="fixed top-20 inset-x-0 z-40 h-px bg-exodus-border">
        <div
          data-testid="quiz-progress-bar"
          className="h-full bg-exodus-gold transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <section className="max-w-3xl mx-auto px-6 lg:px-12 pt-36 pb-24">
        {/* RESUME BANNER */}
        {resumeOffered && step === 0 && (
          <div data-testid="quiz-resume-banner" className="border border-exodus-gold bg-exodus-surface p-6 mb-12 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow gold-text">Resume your assessment</p>
              <p className="text-sm text-exodus-muted mt-1">You left a draft on this device. Pick up where you left off?</p>
            </div>
            <div className="flex gap-3">
              <button data-testid="quiz-resume-btn" onClick={resumeDraft} className="btn-gold inline-flex items-center gap-2 text-xs">
                <ArrowClockwise size={14} /> Resume
              </button>
              <button data-testid="quiz-resume-dismiss" onClick={dismissResume} className="text-xs text-exodus-muted hover:text-exodus-text px-4 py-3">
                Start fresh
              </button>
            </div>
          </div>
        )}

        {/* INTRO with rotating hook */}
        {step === 0 && (
          <div data-testid={`quiz-intro-${introHook.id}`} className="space-y-10">
            <div>
              <p className="eyebrow"><span className="gold-divider" />{introHook.eyebrow}</p>
              <h1 data-testid="quiz-intro-headline" className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95]">
                {introHook.headline}
              </h1>
            </div>
            <p className="text-lg text-exodus-muted leading-relaxed max-w-2xl">{introHook.sub}</p>
            <div className="flex items-center gap-2 text-xs text-exodus-muted">
              <Lock size={14} /> Your answers are private. Only Leon sees them.
            </div>
            <button data-testid="quiz-start" onClick={() => { progressEvent("fit_start"); setStep(1); }} className="btn-gold inline-flex items-center gap-3">
              Begin the assessment <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        )}

        {/* MID-FUNNEL INTERSTITIAL HOOK */}
        {showMidHookHere && (
          <div data-testid="quiz-mid-hook" className="space-y-8">
            <p className="eyebrow"><span className="gold-divider" />{midHook.eyebrow}</p>
            <h2 className="font-display text-5xl lg:text-6xl leading-[0.95]">{midHook.headline}</h2>
            <p className="text-lg text-exodus-muted leading-relaxed max-w-2xl">{midHook.sub}</p>
            <button
              data-testid="quiz-mid-continue"
              onClick={() => { setAnswers((a) => ({ ...a, __mid_seen: true })); }}
              className="btn-gold inline-flex items-center gap-3"
            >
              Continue <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        )}

        {/* QUESTION */}
        {inQuestion && q && !showMidHookHere && (
          <div data-testid={`quiz-step-${step}`} className="space-y-10">
            <div>
              <p className="eyebrow">Step {step} of {totalSteps}</p>
              <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">{q.prompt}</h2>
              {q.subtext && <p className="text-sm text-exodus-muted mt-4 leading-relaxed max-w-2xl">{q.subtext}</p>}
            </div>

            <div className="space-y-2">
              {q.type === "ranked" && (
                <p data-testid="quiz-ranked-hint" className="text-xs text-exodus-muted mb-3">
                  Tap to add in priority order. First pick = top priority. Max {q.max_select || 3}.
                  {Array.isArray(answers[q.id]) && answers[q.id].length > 0 && (
                    <span className="text-exodus-gold ml-2">· {answers[q.id].length} of {q.max_select || 3} ranked</span>
                  )}
                </p>
              )}
              {q.options.map((o) => {
                let rankIndex = -1;
                let selected = false;
                if (q.type === "ranked") {
                  const arr = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                  rankIndex = arr.indexOf(o.value);
                  selected = rankIndex !== -1;
                } else if (q.type === "multi") {
                  selected = Array.isArray(answers[q.id]) && answers[q.id].includes(o.value);
                } else {
                  selected = answers[q.id] === o.value;
                }
                return (
                  <button
                    key={o.value}
                    data-testid={`quiz-option-${q.id}-${o.value}`}
                    onClick={() => setAnswer(q.id, o.value, q.type, q.max_select)}
                    className={`w-full text-left border px-6 py-5 transition-all duration-200 group flex items-center justify-between gap-4 ${
                      selected
                        ? "border-exodus-gold bg-exodus-elevated text-exodus-text"
                        : "border-exodus-border bg-exodus-surface text-exodus-text hover:border-exodus-gold/50 hover:bg-exodus-elevated"
                    }`}
                  >
                    <span className="text-base flex-1">{o.label}</span>
                    {selected && q.type === "ranked" && (
                      <span
                        data-testid={`quiz-rank-badge-${q.id}-${o.value}`}
                        className="flex-shrink-0 w-8 h-8 rounded-full bg-exodus-gold text-exodus-bg font-display text-base flex items-center justify-center"
                      >
                        {rankIndex + 1}
                      </span>
                    )}
                    {selected && q.type !== "ranked" && (
                      <CheckCircle size={20} weight="fill" className="text-exodus-gold flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-4">
              <button data-testid="quiz-back" onClick={back} className="inline-flex items-center gap-2 text-sm text-exodus-muted hover:text-exodus-text transition-colors">
                <ArrowLeft size={14} /> Back
              </button>
              <button data-testid="quiz-next" onClick={next} className="btn-gold inline-flex items-center gap-3">
                {step === totalSteps ? "Continue" : "Next"} <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* IDENTITY */}
        {isIdentity && (
          <div data-testid="quiz-identity" className="space-y-10">
            <div>
              <p className="eyebrow">Last step</p>
              <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
                Where should I send your <span className="gold-text italic">strategic fit?</span>
              </h2>
              <p className="text-sm text-exodus-muted mt-4 leading-relaxed max-w-xl">
                I'll email you a copy of your match. Your phone is helpful if I need to send a calendar invite for the private session.
              </p>
            </div>

            <div className="space-y-3 max-w-md">
              <input data-testid="quiz-name" className="input-exodus" placeholder="Full name"
                value={identity.name} onChange={(e) => setIdentity({ ...identity, name: e.target.value })} />
              <input data-testid="quiz-email" type="email" className="input-exodus" placeholder="Email address"
                value={identity.email} onChange={(e) => setIdentity({ ...identity, email: e.target.value })} />
              <input data-testid="quiz-phone" className="input-exodus" placeholder="Phone (optional but recommended)"
                value={identity.phone} onChange={(e) => setIdentity({ ...identity, phone: e.target.value })} />
            </div>

            <div className="flex items-center justify-between">
              <button data-testid="quiz-back-identity" onClick={back} className="inline-flex items-center gap-2 text-sm text-exodus-muted hover:text-exodus-text">
                <ArrowLeft size={14} /> Back
              </button>
              <button data-testid="quiz-submit" onClick={submit} disabled={submitting}
                className="btn-gold inline-flex items-center gap-3 disabled:opacity-40">
                {submitting ? "Calculating…" : "Reveal my fit"} <Sparkle size={16} weight="fill" />
              </button>
            </div>
          </div>
        )}

        {/* RESULT */}
        {isResult && result && (
          <div data-testid="quiz-result" className="space-y-10">
            <div>
              <p className="eyebrow"><span className="gold-divider" />Your Strategic Fit</p>
              <h2 data-testid="quiz-result-headline" className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
                Your next step is <span className="gold-text italic">simple.</span>
              </h2>
              <p data-testid="quiz-result-line" className="text-lg text-exodus-text mt-6 max-w-2xl">
                Based on your answers, you're a fit for a <span className="gold-text">{LANE_NAME[result.primary || laneFromLabel(result.primary_label)]}</span> strategy.
              </p>
              <p data-testid="quiz-result-clarifier" className="text-sm text-exodus-muted mt-3 max-w-2xl">
                This doesn't mean you should buy anything today. It means you now know what to test.
              </p>
            </div>

            <div data-testid="quiz-result-primary" className="border border-exodus-gold p-8 lg:p-10 bg-exodus-surface">
              <p className="eyebrow gold-text">Primary fit — {result.tier === "high" ? "high-funded design" : "conservative design"}</p>
              <h3 className="font-display text-4xl lg:text-5xl mt-3">{result.primary_label}</h3>
              {result.secondary && (
                <p className="text-sm text-exodus-muted mt-4">
                  Secondary consideration: <span className="text-exodus-text">{result.secondary}</span>
                </p>
              )}
            </div>

            <div>
              <p className="eyebrow mb-4">Why this fits</p>
              <ul className="space-y-3">
                {result.rationale.map((r, i) => (
                  <li key={i} className="flex gap-3 text-sm text-exodus-text leading-relaxed">
                    <span className="text-exodus-gold flex-shrink-0">—</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-exodus-border pt-10 space-y-5">
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-start">
                <Link data-testid="quiz-cta-see-numbers" to={`/calculator?product=${result.primary || laneFromLabel(result.primary_label)}`} className="btn-gold inline-flex items-center gap-3 w-full sm:w-auto justify-center">
                  Run the Tax-Free Calculator (3 minutes) <ArrowRight size={16} weight="bold" />
                </Link>
                <div className="w-full sm:w-auto">
                  <Link data-testid="quiz-cta-reserve" to="/reserve" onClick={() => progressEvent("book_click")} className="btn-ghost inline-flex items-center gap-3 w-full sm:w-auto justify-center">
                    {BOOK_LABEL} <ArrowRight size={16} weight="bold" />
                  </Link>
                  <p data-testid="quiz-cta-reserve-microcopy" className="text-xs text-exodus-muted mt-2 max-w-xs">{BOOK_FEE_LINE}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                {result.next_step === "concierge" && (
                  <Link data-testid="quiz-cta-concierge" to="/concierge" className="text-sm text-exodus-muted hover:text-exodus-gold transition-colors px-4 py-3 border border-exodus-border hover:border-exodus-gold">
                    Open a concierge inquiry
                  </Link>
                )}
                <Link data-testid="quiz-cta-learn" to="/learn" className="text-sm text-exodus-muted hover:text-exodus-gold transition-colors px-4 py-3 border border-exodus-border hover:border-exodus-gold">
                  Read the library first
                </Link>
                {result.brief_pdf_url && (
                  <a
                    data-testid="quiz-cta-pdf"
                    href={`${process.env.REACT_APP_BACKEND_URL}${result.brief_pdf_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm gold-text hover:text-exodus-text transition-colors px-4 py-3 border border-exodus-gold hover:bg-exodus-surface"
                  >
                    Download your Strategy Brief (PDF)
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <Footer />
      <FloatingBookButton />
    </div>
  );
}
